import { styled, keyframes } from '@mui/material/styles';
import { Box, Typography, Button, TableCell, TableRow, Switch } from '@mui/material';

// ============================================
// Page Container (gray background)
// ============================================
export const PageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

// ============================================
// Dashboard Cards
// ============================================
// 폭이 좁으면 카드 간격/여백/아이콘을 한 단계 줄여서 6장을 한 줄에 유지한다.
// (스크롤보다 카드가 줄어드는 쪽이 낫다는 요구)
const COMPACT = '@media (max-width: 1660px)';
const DASHBOARD_MIN_WIDTH = 1216;

export const DashboardScroller = styled(Box)({
  overflowX: 'auto',
  overflowY: 'hidden',
  paddingBottom: '4px',
});

// 최소 폭까지만 줄어들고, 그 아래로는 DashboardScroller 가 가로 스크롤한다.
export const DashboardStack = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  minWidth: `${DASHBOARD_MIN_WIDTH}px`,
  [COMPACT]: { gap: '16px' },
});

// 1행 4칸 / 2행 (카드묶음 3 + 버튼영역 1) 4칸을 항상 같은 폭으로 맞춘다.
// flex 로는 padding 이 있는 버튼영역이 더 넓어져 열이 어긋나므로 grid 를 쓴다.
export const DashboardRow = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: '24px',
  [COMPACT]: { gap: '16px' },
});

// 2행: 카드 2장 묶음이 1행 카드 1장과 같은 폭을 갖도록 균등 분배
export const DashboardCardGroup = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '24px',
  [COMPACT]: { gap: '16px' },
});

export const DashboardCard = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '24px',
  backgroundColor: '#fff',
  borderRadius: '16px',
  boxShadow: '0px 0px 4px rgba(0,0,0,0.08)',
  minWidth: 0,
  [COMPACT]: { gap: '10px', padding: '16px' },
});

export const DashboardIconBox = styled(Box, {
  shouldForwardProp: prop => prop !== 'bgColor',
})(({ bgColor }) => ({
  width: '48px',
  height: '48px',
  minWidth: '48px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: bgColor || 'transparent',
  '& svg': { fontSize: '32px' },
  [COMPACT]: {
    width: '40px',
    height: '40px',
    minWidth: '40px',
    '& svg': { fontSize: '26px' },
  },
}));

export const DashboardLabel = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#737373',
  whiteSpace: 'nowrap',
});

export const DashboardValue = styled(Typography)({
  fontSize: '20px',
  fontWeight: 600,
  color: '#404040',
  whiteSpace: 'nowrap',
});

// Dashboard row 2: buttons area
export const DashboardButtonArea = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: '24px',
  [COMPACT]: { padding: '16px' },
  backgroundColor: '#fff',
  borderRadius: '16px',
  boxShadow: '0px 0px 4px rgba(0,0,0,0.08)',
});

export const DashboardButtonGroup = styled(Box)({
  display: 'flex',
  gap: '12px',
  flex: '1 1 0',
  minWidth: 0,
});

export const PositionManageButton = styled(Button)({
  flex: '1 1 0',
  minWidth: 0,
  whiteSpace: 'nowrap',
  fontSize: '16px',
  fontWeight: 600,
  borderRadius: '8px',
  height: '44px',
  backgroundColor: '#404040',
  color: '#fff',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#333',
  },
});

export const CreateButton = styled(Button)({
  flex: '1 1 0',
  minWidth: 0,
  whiteSpace: 'nowrap',
  fontSize: '16px',
  fontWeight: 600,
  borderRadius: '8px',
  height: '44px',
  backgroundColor: '#4ab3bc',
  color: '#fff',
  textTransform: 'none',
  gap: '8px',
  '&:hover': {
    backgroundColor: '#3a9da5',
  },
});

// ============================================
// Table Card
// ============================================
export const TableCard = styled(Box)({
  backgroundColor: '#fff',
  borderRadius: '16px',
  boxShadow: '0px 0px 4px rgba(0,0,0,0.08)',
  padding: '24px',
});

export const TableHeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

export const TableTitle = styled(Typography)({
  fontSize: '20px',
  fontWeight: 600,
  color: '#404040',
  whiteSpace: 'nowrap',
});

export const TableTitleSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

export const SearchField = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '320px',
  height: '44px',
  padding: '0 16px',
  border: '1px solid #d9d9d9',
  borderRadius: '8px',
  backgroundColor: '#fff',
  cursor: 'text',
});

export const SearchInput = styled('input')({
  border: 'none',
  outline: 'none',
  flex: 1,
  fontSize: '16px',
  fontFamily: 'Pretendard',
  fontWeight: 500,
  color: '#404040',
  '&::placeholder': {
    color: '#a6a6a6',
  },
});

export const FilterSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

export const ToggleLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#404040',
  whiteSpace: 'nowrap',
});

export const TealSwitch = styled(Switch)({
  width: '40px',
  height: '20px',
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: '2px',
    '&.Mui-checked': {
      transform: 'translateX(20px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#4ab3bc',
        opacity: 1,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: '16px',
    height: '16px',
    boxShadow: 'none',
  },
  '& .MuiSwitch-track': {
    borderRadius: '10px',
    backgroundColor: '#d9d9d9',
    opacity: 1,
  },
});

// ============================================
// Table
// ============================================
export const StyledHeaderCell = styled(TableCell)({
  fontWeight: 600,
  fontSize: '14px',
  color: '#737373',
  padding: '0 16px',
  borderRight: '1px solid #f2f2f2',
  borderBottom: 'none',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  '&:last-child': {
    borderRight: 'none',
  },
});

export const StyledBodyRow = styled(TableRow, {
  shouldForwardProp: prop => prop !== 'clickable' && prop !== 'retired',
})(({ clickable, retired }) => ({
  height: '40px',
  textDecoration: 'none',
  cursor: clickable ? 'pointer' : 'default',
  ...(retired && {
    backgroundColor: '#fafafa',
    '& td': {
      color: '#bbb',
    },
  }),
  '&:hover': {
    backgroundColor: clickable ? (retired ? '#f3f3f3' : '#f5f9fa') : 'inherit',
  },
}));

export const StyledBodyCell = styled(TableCell)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#333',
  padding: '0 16px',
  borderRight: '1px solid #f2f2f2',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  '&:last-child': {
    borderRight: 'none',
  },
});

// ============================================
// Salary Type Chip
// ============================================
const SALARY_CHIP_COLORS = {
  횟수제: { bg: '#55c982', color: '#fff' },
  월급제: { bg: '#3582c0', color: '#fff' },
  시급제: { bg: '#ffb659', color: '#fff' },
  일급제: { bg: '#e85d5d', color: '#fff' },
};

export const SalaryChip = styled(Typography, {
  shouldForwardProp: prop => prop !== 'salaryType',
})(({ salaryType }) => {
  const colors = SALARY_CHIP_COLORS[salaryType] || { bg: '#d9d9d9', color: '#666' };
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 600,
    padding: '0 8px',
    height: '24px',
    borderRadius: '4px',
    color: colors.color,
    backgroundColor: colors.bg,
    whiteSpace: 'nowrap',
  };
});

export const StatusChip = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isWorking',
})(({ isWorking }) => ({
  display: 'inline-block',
  fontSize: '14px',
  fontWeight: 500,
  color: isWorking ? '#262626' : '#bbb',
}));

export const EmptyMessage = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '60px 0',
  fontSize: '14px',
  color: '#999',
});

// ============================================
// Guide Button
// ============================================
const guideSlideIn = keyframes`
  0% { transform: translateX(30px); opacity: 0; }
  40% { transform: translateX(-5px); opacity: 1; }
  60% { transform: translateX(3px); }
  80% { transform: translateX(-1px); }
  100% { transform: translateX(0); }
`;

const guideShine = keyframes`
  0%, 100% { background-position: -200% center; }
  50% { background-position: 200% center; }
`;

const pulseGlow = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(74, 179, 188, 0.4); }
  50% { box-shadow: 0 0 0 6px rgba(74, 179, 188, 0); }
  100% { box-shadow: 0 0 0 0 rgba(74, 179, 188, 0); }
`;

export const GuideButton = styled(Button)({
  fontSize: '13px',
  fontWeight: 500,
  fontFamily: 'Pretendard, sans-serif',
  borderRadius: '20px',
  padding: '4px 14px',
  minWidth: 0,
  whiteSpace: 'nowrap',
  textTransform: 'none',
  background: 'linear-gradient(120deg, #4AB3BC 0%, #6DD5DC 40%, #fff 50%, #6DD5DC 60%, #4AB3BC 100%)',
  backgroundSize: '200% 100%',
  color: '#fff',
  border: '1px solid transparent',
  animation: `${guideSlideIn} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, ${guideShine} 3s ease-in-out 1s infinite, ${pulseGlow} 2s ease-in-out 1s 3`,
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'linear-gradient(120deg, #3FA0A8 0%, #4AB3BC 100%)',
    border: '1px solid transparent',
    transform: 'scale(1.05)',
    color: '#fff',
  },
});
